import { Page, AnimatedReveal } from "@iq/models";
import { useEffect, useMemo, useState } from "react";
import CalendarComponent, { getWeekDates, resolveAppointmentDateKey } from "../components/Calendar/Calendar";
import FollowUpBookingModal from "../components/followUp/FollowUpBookingModal";
import CompleteAppointmentModal from "../components/completeAppointment/CompleteAppointmentModal";
import MiniMonth from "../components/MiniMonth/MiniMonth";
import AppointmentList from "../components/AppointmentList/AppointmentList";
import AppointmentSidebar from "../components/AppointmentSidebar/AppointmentSidebar";
import useAppointmentStore from "../states/appointment";
import useUserStore from "../states/user";
import { issueConfirmToken, CaptureRemainingBalance, deleteAppointment, markAppointmentPaidCash } from "../services/AppointmentService";
import { appointmentAction } from "../utils/appointmentAction";
import styles from "./CalendarPage.module.css";
import LoadingScreen from "../components/LoadingScreen/LoadingScreen";

const STRIVER_BASE_URL = String(process.env.REACT_APP_STRIVER_BASE_URL || "http://localhost:3009").replace(/\/$/, "");

function getTodayKey(date = new Date()) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function getMonthKey(date = new Date()) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
}

function getWeekLabel(dateValue) {
    const base = new Date(`${dateValue}T12:00:00`);
    const start = new Date(base);
    const end = new Date(base);
    const day = base.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    start.setDate(base.getDate() + diff);
    end.setDate(start.getDate() + 6);
    const formatter = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" });
    return `${formatter.format(start)} – ${formatter.format(end)}`;
}

function getAppointmentKey(appointment) {
    return appointment?.id ?? appointment?.hashed_appointment_id ?? `${appointment?.date || appointment?.appointment_date || "unknown"}-${appointment?.start_time || "time"}-${appointment?.customer?.name || appointment?.customer_name || "customer"}`;
}

function isCompleted(appointment) {
    const status = String(appointment?.status || "").trim().toLowerCase();
    const payment = String(appointment?.payment_status || "").trim().toLowerCase();
    return status === "completed" || payment === "completed";
}

function isCancelled(appointment) {
    return String(appointment?.status || "").trim().toLowerCase() === "cancelled";
}

function CalendarPage() {
    const [selectedDate, setSelectedDate] = useState(getTodayKey());
    const [selectedAppointmentId, setSelectedAppointmentId] = useState("");
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [listMode, setListMode] = useState("pending");
    const [depositError, setDepositError] = useState("");
    const [busyMessage, setBusyMessage] = useState("");
    const userId = useUserStore((state) => state.session?.profile?.id);
    const appointments = useAppointmentStore((state) => state.appointments);
    const loadedMonth = useAppointmentStore((state) => state.month);
    const loading = useAppointmentStore((state) => state.loading);
    const error = useAppointmentStore((state) => state.error);
    const fetchAppointments = useAppointmentStore((state) => state.fetchAppointments);
    const [verification, setVerification] = useState(null);
    const [followUpAppointment, setFollowUpAppointment] = useState(null);
    const [completingAppointment, setCompletingAppointment] = useState(null);
    const selectedMonth = getMonthKey(new Date(`${selectedDate}T12:00:00`));

    useEffect(() => {
        if (!userId || loadedMonth === selectedMonth) return;
        fetchAppointments(userId, selectedMonth);
    }, [userId, selectedMonth, loadedMonth, fetchAppointments]);

    useEffect(() => {
        const refreshOnFocus = () => {
            if (userId) fetchAppointments(userId, selectedMonth);
        };
        window.addEventListener("focus", refreshOnFocus);
        return () => window.removeEventListener("focus", refreshOnFocus);
    }, [userId, selectedMonth, fetchAppointments]);

    const weekDates = useMemo(() => getWeekDates(selectedDate || getTodayKey()), [selectedDate]);
    const weekDateKeys = useMemo(() => weekDates.map((date) => getTodayKey(date)), [weekDates]);

    const markedDates = useMemo(
        () => new Set(appointments.map(resolveAppointmentDateKey).filter(Boolean)),
        [appointments],
    );

    const listedAppointments = useMemo(() => appointments
        .filter((appointment) => resolveAppointmentDateKey(appointment) === selectedDate && !isCancelled(appointment))
        .filter((appointment) => (listMode === "completed") === isCompleted(appointment))
        .sort((first, second) => (first?.start_time || "").localeCompare(second?.start_time || "")),
    [appointments, selectedDate, listMode]);

    const selectedAppointment = useMemo(
        () => appointments.find((appointment) => getAppointmentKey(appointment) === selectedAppointmentId) || null,
        [appointments, selectedAppointmentId],
    );
    const selectedAction = appointmentAction(selectedAppointment);

    function handleSelectAppointment(appointment) {
        setSelectedAppointmentId(getAppointmentKey(appointment));
        setSidebarOpen(true);
    }

    function shiftWeek(direction) {
        const current = new Date(`${selectedDate || getTodayKey()}T12:00:00`);
        current.setDate(current.getDate() + direction * 7);
        setSelectedDate(getTodayKey(current));
    }

    async function handleCollectDeposit(hashedAppointmentID) {
        setDepositError("");
        if (!hashedAppointmentID) {
            setDepositError("This appointment has no confirm link.");
            return;
        }

        const tab = window.open("", "_blank");
        if (tab) tab.opener = null;

        setBusyMessage("Opening appointment confirmation...");
        try {
            const { token } = await issueConfirmToken(hashedAppointmentID);
            const url = `${STRIVER_BASE_URL}/confirm/appointment/${encodeURIComponent(hashedAppointmentID)}?token=${encodeURIComponent(token)}`;
            if (tab) {
                tab.location.href = url;
            } else {
                window.location.assign(url);
            }
        } catch (err) {
            tab?.close();
            setDepositError(err.message);
        } finally {
            setBusyMessage("");
        }
    }

    async function redirectToPaymentFailure(hashedAppointmentID) {
        const { token } = await issueConfirmToken(hashedAppointmentID);
        const params = new URLSearchParams({
            hashed_appointment_id: hashedAppointmentID,
            token,
        });
        window.location.assign(`${STRIVER_BASE_URL}/payment-failure?${params.toString()}`);
    }

    async function handleDeleteAppointment(appointmentID) {
        if (!appointmentID) {
            setDepositError("This appointment is missing its ID.");
            return;
        }

        if (!window.confirm("Delete this appointment and its related records? This cannot be undone.")) {
            return;
        }

        setDepositError("");
        setBusyMessage("Deleting appointment...");

        try {
            await deleteAppointment(appointmentID);
            await fetchAppointments(userId, selectedMonth);
        } catch (err) {
            setDepositError(err.message);
        } finally {
            setBusyMessage("");
        }
    }

    async function handleCompleteAppointment(request) {
        if (request?.type === "card") {
            return CaptureRemainingBalance(request.appointment_id);
        }

        if (request?.type === "cash") {
            return markAppointmentPaidCash(request.appointment_id);
        }

        throw new Error(`${request?.type || "Unknown"} completion isn't connected yet.`);
    }

    async function handleCaptureRemainingBalance(appointmentID, hashedAppointmentID, simulateFailure = false) {
        setDepositError("");
        if (!appointmentID || !hashedAppointmentID) {
            setDepositError("This appointment is missing its ID or failure-page link.");
            return;
        }

        if (simulateFailure) {
            try {
                await redirectToPaymentFailure(hashedAppointmentID);
            } catch (err) {
                setDepositError(err.message);
            }
            return;
        }

        try {
            const result = await CaptureRemainingBalance(appointmentID);
            setVerification(result);
        } catch {
            try {
                await redirectToPaymentFailure(hashedAppointmentID);
            } catch (tokenError) {
                setDepositError(`Payment failed and the failure link could not be issued: ${tokenError.message}`);
            }
        }
    }

    return (
        <AnimatedReveal>
            <Page>
                <LoadingScreen open={Boolean(busyMessage)} message={busyMessage} />
                <div className={styles.content}>
                    <aside className={styles.sidePanel}>
                        <MiniMonth
                            selectedDate={selectedDate}
                            weekDateKeys={weekDateKeys}
                            markedDates={markedDates}
                            onSelectDate={setSelectedDate}
                        />
                        <AppointmentList
                            appointments={listedAppointments}
                            mode={listMode}
                            onModeChange={setListMode}
                            selectedDate={selectedDate}
                            todayKey={getTodayKey()}
                            selectedKey={selectedAppointment ? getAppointmentKey(selectedAppointment) : ""}
                            onSelect={handleSelectAppointment}
                            getKey={getAppointmentKey}
                            getDateKey={resolveAppointmentDateKey}
                        />
                    </aside>
                    <div className={styles.calendarPanel}>
                        <div className={styles.calendarPanelHeader}>
                            <h2 data-reveal>{getWeekLabel(selectedDate)}</h2>
                            <button
                                type="button"
                                onClick={() => setSelectedDate(getTodayKey())}
                                className={styles.todayButton}
                            >
                                Today
                            </button>
                        </div>

                        {error ? <p className={styles.error} role="alert">{error}</p> : null}
                        {depositError ? <p className={styles.error} role="alert">{depositError}</p> : null}

                        <div className={styles.weekCalendarBody}>
                            <CalendarComponent
                                appointments={appointments}
                                selectedMonth={selectedMonth}
                                selectedDate={selectedDate}
                                onSelectDate={setSelectedDate}
                                onSelectAppointment={handleSelectAppointment}
                                view="week"
                                weekDates={weekDates}
                            />
                        </div>
                    </div>


                </div>

                <AppointmentSidebar
                    appointment={selectedAppointment}
                    dateKey={selectedAppointment ? resolveAppointmentDateKey(selectedAppointment) : null}
                    action={selectedAction}
                    verification={verification}
                    isOpen={sidebarOpen && Boolean(selectedAppointment)}
                    onClose={() => setSidebarOpen(false)}
                    onComplete={(appointment) => {
                        setSidebarOpen(false);
                        setCompletingAppointment(appointment);
                    }}
                    onFollowUp={(appointment) => {
                        setSidebarOpen(false);
                        setFollowUpAppointment(appointment);
                    }}
                    onConfirm={(appointment) => handleCollectDeposit(appointment.hashed_appointment_id)}
                    onDelete={(appointment) => handleDeleteAppointment(appointment.id)}
                />

                <FollowUpBookingModal
                    isOpen={Boolean(followUpAppointment)}
                    appointment={followUpAppointment}
                    userID={userId}
                    onClose={() => setFollowUpAppointment(null)}
                    onBooked={() => fetchAppointments(userId, selectedMonth)}
                />

                <CompleteAppointmentModal
                    isOpen={Boolean(completingAppointment)}
                    appointment={completingAppointment}
                    onClose={() => setCompletingAppointment(null)}
                    onSubmit={handleCompleteAppointment}
                    onCompleted={() => fetchAppointments(userId, selectedMonth)}
                />
        </Page>
        </AnimatedReveal>
    );
}

export default CalendarPage;